import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from '@/lib/api'
import { messages } from '@/lib/messages'
import { ApiError } from '@/lib/problem'
import { queryKeys } from '@/lib/query-keys'
import type { components } from '@/types/api'

/*
 * `/api/v1/bi/reports*` (docs/contracts.md §12.1): kayıtlı rapor tanımları ve sürümleri.
 * Belge şablonu `definition.kind === "document"` olan rapordur.
 */

export type BiReport = components['schemas']['ReportRead']
export type BiReportVersion = components['schemas']['ReportVersionSummary']
export type Visibility = BiReport['visibility']

const BASE = '/bi/reports'

/** Aynı sürümü başkası da kaydetti (`expected_version` uyuşmadı, 409). */
export class VersionConflictError extends Error {
  constructor() {
    super(messages.documents.editor.conflict)
    this.name = 'VersionConflictError'
  }
}

export const isVersionConflict = (error: unknown) => error instanceof VersionConflictError

export const listReports = () => apiFetch<BiReport[]>(BASE)

export const getReport = (id: string) => apiFetch<BiReport>(`${BASE}/${id}`)

export const createReport = (body: { definition: object; visibility?: Visibility }) =>
  apiFetch<BiReport>(BASE, { method: 'POST', body })

export async function updateReport(
  id: string,
  body: { definition: object; expected_version: number; visibility?: Visibility },
): Promise<BiReport> {
  try {
    return await apiFetch<BiReport>(`${BASE}/${id}`, { method: 'PUT', body })
  } catch (error) {
    if (error instanceof ApiError && error.status === 409) throw new VersionConflictError()
    throw error
  }
}

export const listVersions = (id: string) => apiFetch<BiReportVersion[]>(`${BASE}/${id}/versions`)

export const restoreVersion = (id: string, version: number) =>
  apiFetch<BiReport>(`${BASE}/${id}/versions/${version}/restore`, { method: 'POST' })

/** Kullanıcının görebildiği belge şablonları (backend görünürlüğü süzer; tür istemcide). */
export function useDocumentTemplates() {
  return useQuery({
    queryKey: queryKeys.bi.reports(),
    queryFn: listReports,
    select: (reports) => reports.filter((report) => report.definition.kind === 'document'),
  })
}

export function useBiReport(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.bi.report(id ?? ''),
    queryFn: () => getReport(id as string),
    enabled: id !== undefined,
    // Editör kendi kopyasını tutar; arka planda yeniden çekilip ezilmesin.
    staleTime: Infinity,
  })
}

/** Sürüm listesi yalnız düzenleyenlere açıktır (403 -> `enabled: false` ile hiç sorulmaz). */
export function useReportVersions(id: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.bi.versions(id),
    queryFn: () => listVersions(id),
    enabled,
  })
}

export function useCreateReport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createReport,
    onSuccess: (report) => {
      queryClient.setQueryData(queryKeys.bi.report(report.id), report)
      void queryClient.invalidateQueries({ queryKey: queryKeys.bi.reports() })
    },
  })
}

/** Yeni sürüm yazar; 409'da `VersionConflictError` ("başkası değiştirdi, yeniden yükle"). */
export function useSaveReport(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: Parameters<typeof updateReport>[1]) => updateReport(id, body),
    onSuccess: (report) => {
      queryClient.setQueryData(queryKeys.bi.report(id), report)
      void queryClient.invalidateQueries({ queryKey: queryKeys.bi.reports() })
      void queryClient.invalidateQueries({ queryKey: queryKeys.bi.versions(id) })
    },
  })
}

export function useRestoreVersion(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (version: number) => restoreVersion(id, version),
    onSuccess: (report) => {
      queryClient.setQueryData(queryKeys.bi.report(id), report)
      void queryClient.invalidateQueries({ queryKey: queryKeys.bi.reports() })
      void queryClient.invalidateQueries({ queryKey: queryKeys.bi.versions(id) })
    },
  })
}
